import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import { DialogClose } from '@/components/DialogClose';
import type { ReactNode } from 'react';
import type { Breakpoint } from '@mui/material/styles';

type Props = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Grey line under the title, e.g. "Rohan Mehta · EMP003 · FY 2026-27". */
  subtitle?: ReactNode;
  /** Slot left of the title — e.g. a back button for a multi-step popup. */
  back?: ReactNode;
  /** Footer buttons. Rendered in the sticky footer; omit for no footer. */
  actions?: ReactNode;
  children: ReactNode;
  /** Fixed paper width in px (wireframe dialogs are 560 to 880). Wins over maxWidth. */
  width?: number;
  maxWidth?: Breakpoint | false;
  fullWidth?: boolean;
  /** While a save is in flight: blocks backdrop/esc/close so nothing is lost. */
  busy?: boolean;
  /** Body is scrollable with divider rules by default; turn off for plain content. */
  dividers?: boolean;
};

/**
 * UiDialog — the ONE modal for the app. Gives every popup the wireframe chrome
 * (12px corners, 18px title, optional grey subtitle, close X, padded body,
 * footer actions) from the theme, so a change there reaches every popup.
 */
export function UiDialog({
  open, onClose, title, subtitle, back, actions, children, width,
  maxWidth = 'md', fullWidth = true, busy = false, dividers = true,
}: Props) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      disableEscapeKeyDown={busy}
      maxWidth={width ? false : maxWidth}
      fullWidth={width ? false : fullWidth}
      PaperProps={width ? { sx: { width, maxWidth: 'calc(100% - 32px)' } } : undefined}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {back}
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          {title}
          {subtitle && (
            <Box sx={{ fontSize: 13, fontWeight: 400, color: 'text.secondary', mt: 0.5, lineHeight: 1.5 }}>{subtitle}</Box>
          )}
        </Box>
        <DialogClose onClose={onClose} />
      </DialogTitle>
      <DialogContent dividers={dividers}>{children}</DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  );
}
